// El REGISTRO de cada herramienta que el agente usó, para los turnos siguientes —
// ADR-2026-09-13-todo-lo-que-uso-el-agente-queda-registrado.
//
// Los `tool_use` y `tool_result` de una ronda no se vuelven a mandar en los turnos
// siguientes (serían decenas de imágenes y de bloques arrastrados en todo el historial).
// Las herramientas con card ya dejaban un marcador con sus ids (lo generado, el lote, el
// PDF…); las SILENCIOSAS —lo que miró con ver_productos / ver_fotos / ver_moodboard, lo
// que leyó de una planilla, el análisis de un video, la skill que cargó, qué eligió el
// usuario en pick_product— no dejaban NADA: al turno siguiente el agente no sabía qué
// había mirado ni qué se había elegido. Acá se arma una línea compacta por llamada, con
// los ids que trae el resultado (no las imágenes), y se cuelga del mensaje del asistente
// para viajar en la nota del sistema del turno siguiente.
//
// Puro: (nombre, input, resultado) → una línea o null.

const colapsar = (s: string): string => s.replace(/\s+/g, ' ').trim();
const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Las que YA dejan su propio marcador desde la card (no se duplican) o no aportan nada. */
const YA_REGISTRADAS = new Set([
  'generate_image', 'confirm_generate', 'generate_video', 'generate_audio', 'generar_lote_imagenes',
  'agregar_productos', 'crear_pdf', 'crear_planilla', 'mostrar_guardado', 'preguntar', 'show_recent_media',
  'mostrar_respuesta_grafica', 'sugerir_acciones',
]);

/** Cuánto del resultado se conserva. El análisis de un video ES el valor: se guarda más. */
// `abrir_web` y `extraer_fotos_del_pdf`: el resumen con los adjunto_id de las imágenes guardadas es
// lo que hay que recordar (hasta 100 fotos de un PDF: "12=pdf-… (p4)" son ~26 caracteres cada una, ~2.900 en total).
const TOPE_RESULTADO: Record<string, number> = { ver_video: 900, ver_planilla: 500, ver_pdf: 500, abrir_web: 700, extraer_fotos_del_pdf: 3200 };
const TOPE_POR_DEFECTO = 400;
const TOPE_INPUT = 200;

/** El input sin lo que no escribió el modelo, en una línea corta. */
export function inputCompacto(input: unknown): string {
  if (!input || typeof input !== 'object') return '';
  const { autogenerate: _fuera, ...resto } = input as Record<string, unknown>;
  const claves = Object.keys(resto);
  if (claves.length === 0) return '';
  return clip(colapsar(JSON.stringify(resto)), TOPE_INPUT);
}

/** El resultado como texto corto: los bloques de texto (traen los ids), el texto, o el JSON. */
export function resultadoCompacto(resultado: unknown, tope: number): string {
  if (resultado == null) return '';
  if (typeof resultado === 'string') return clip(colapsar(resultado), tope);
  if (typeof resultado !== 'object') return clip(String(resultado), tope);
  const r = resultado as { __bloques?: unknown; __texto?: unknown; cancelled?: unknown };
  if (Array.isArray(r.__bloques)) {
    const textos = r.__bloques
      .map((b) => (b && typeof b === 'object' && (b as { type?: string }).type === 'text' ? String((b as { text?: unknown }).text ?? '') : ''))
      .filter(Boolean);
    return clip(colapsar(textos.join(' ')), tope);
  }
  if (typeof r.__texto === 'string') return clip(colapsar(r.__texto), tope);
  if (r.cancelled === true) return 'el usuario canceló';
  return clip(colapsar(JSON.stringify(resultado)), tope);
}

/**
 * La línea del registro, o null si esa herramienta ya deja la suya. Empieza con "usaste"
 * para que se lea como lo que es: algo que el agente hizo, contado por el sistema.
 */
export function registroDeHerramienta(nombre: string, input: unknown, resultado: unknown): string | null {
  if (!nombre || YA_REGISTRADAS.has(nombre)) return null;
  if (nombre === 'cargar_skill') {
    const n = (input as { nombre?: unknown } | null)?.nombre;
    const cual = typeof n === 'string' && n.trim() ? `"${n.trim()}"` : '(sin nombre)';
    // El cuerpo de la skill NO queda en el historial: sin esta aclaración el agente cree
    // que todavía lo tiene (el prompt lo dice) y arma el pedido de memoria.
    return `[usaste cargar_skill: ${cual}. Su texto ya no está en el historial: si la necesitás en este turno, cargala de nuevo.]`;
  }
  const entrada = inputCompacto(input);
  const salida = resultadoCompacto(resultado, TOPE_RESULTADO[nombre] ?? TOPE_POR_DEFECTO);
  return `[usaste ${nombre}${entrada ? ` con ${entrada}` : ''}${salida ? ` → ${salida}` : ''}]`;
}
